import { useI18n } from 'vue-i18n'
import { ref, type Ref } from 'vue'
import { useBetslipStore } from '@/stores/betslip'
import { useGameStore } from '@/stores/game'
import { useTransactionsStore } from '@/stores/transactions'
import { useModalStore } from '@/stores/modal'
import { useToast } from '@/composables/useToast'
import { iframeCommunication } from '@/utils/iframeCommunication'
import { resolveRebetDraws, type UpcomingDrawOption } from '@/utils/rebetDraws'
import type { BetslipItem } from '@/stores/betslip'
import { mergeRebetDraws, rebetLineKey } from '@/utils/rebetLines'

// Both entry points share a lock, scoped to their betslip store.
const rebetStates = new WeakMap<object, Ref<boolean>>()

function toBetslipDraws(draws: UpcomingDrawOption[]): BetslipItem['draws'] {
  return draws.map((draw) => ({
    draw_date: draw.draw_date,
    cutoff: draw.cutoff ?? draw.draw_date,
    event_id: draw.event_id,
  }))
}

function matchWagerBetToMarket(bet: Record<string, unknown>, marketGroups: Array<Record<string, any>>) {
  const betMarketId = bet.market_id
  const betOutcomeId = bet.outcome_id
  const betGroupId = bet.market_group_id
  const betGameMarketId = bet.game_market_id

  if (betMarketId != null && betOutcomeId != null) {
    for (const marketGroup of marketGroups) {
      if (betGroupId != null && String(marketGroup.id) !== String(betGroupId)) {
        continue
      }
      for (const market of marketGroup.markets ?? []) {
        if (String(market.id) !== String(betMarketId)) {
          continue
        }
        for (const outcome of market.outcomes ?? []) {
          if (String(outcome.id) === String(betOutcomeId)) {
            return { matchedMarket: market, matchedOutcome: outcome, matchedMarketGroup: marketGroup }
          }
        }
      }
    }
  }

  if (betGameMarketId != null) {
    for (const marketGroup of marketGroups) {
      for (const market of marketGroup.markets ?? []) {
        if (String(market.id) !== String(betGameMarketId)) {
          continue
        }
        for (const outcome of market.outcomes ?? []) {
          if (betOutcomeId != null && String(outcome.id) === String(betOutcomeId)) {
            return { matchedMarket: market, matchedOutcome: outcome, matchedMarketGroup: marketGroup }
          }
        }
      }
    }
  }

  for (const marketGroup of marketGroups) {
    for (const market of marketGroup.markets ?? []) {
      if (market.name !== bet.market_name || !market.outcomes) {
        continue
      }
      for (const outcome of market.outcomes) {
        if (Math.abs(parseFloat(outcome.odds) - parseFloat(String(bet.odds))) < 0.01) {
          return { matchedMarket: market, matchedOutcome: outcome, matchedMarketGroup: marketGroup }
        }
      }
    }
  }

  return null
}

export type ApplyRebetOptions = {
  /** Called after rebet lines were added (e.g. close details modal). */
  onComplete?: () => void
  /** Open the betslip drawer/modal (wagers flow). Default true. */
  openBetslip?: boolean
}

export function useRebetFromTransaction() {
  const { t } = useI18n()
  const { showToast } = useToast()
  const transactionsStore = useTransactionsStore()
  const gameStore = useGameStore()
  const betslipStore = useBetslipStore()
  const modalStore = useModalStore()
  let isRebetting = rebetStates.get(betslipStore)
  if (!isRebetting) {
    isRebetting = ref(false)
    rebetStates.set(betslipStore, isRebetting)
  }
  const rebetting = isRebetting

  const applyRebetFromCurrentTransaction = async (options: ApplyRebetOptions = {}) => {
    if (rebetting.value) return
    rebetting.value = true
    const { onComplete, openBetslip = true } = options

    try {
      const transaction = transactionsStore.currentTransaction
      if (!transaction?.games) {
        showToast({
          title: t('betslip.rebetUnavailableTitle'),
          message: t('betslip.noTransactionData'),
          type: 'error',
          duration: 6000,
        })
        return
      }

      const preparedLines = new Map<string, BetslipItem>()
      let skippedUnavailableDraws = 0

      for (const [gameKey, games] of Object.entries(transaction.games)) {
        await gameStore.fetchGameDetails(gameKey)
        const upcomingDraws = gameStore.getValidDraws
        const marketGroups = gameStore.getMarketGroups
        let closedDrawFallbackIndex = 0
        for (const game of games as any[]) {
          const selectedDraws = resolveRebetDraws(game, upcomingDraws, {
            closedDrawFallbackIndex,
          })
          const targetEventId = Number(game.event_id ?? game.draw_id)
          const matchedOriginalEvent =
            Number.isFinite(targetEventId) &&
            targetEventId > 0 &&
            upcomingDraws.some((draw) => draw.event_id === targetEventId)
          if (
            selectedDraws.length > 0 &&
            !matchedOriginalEvent &&
            game.draw_date &&
            Date.parse(game.draw_date) <= Date.now()
          ) {
            closedDrawFallbackIndex += 1
          }

          if (selectedDraws.length === 0) {
            skippedUnavailableDraws += game.bets?.length ?? 0
            console.warn(
              'Original draw no longer available for rebet:',
              gameKey,
              game.draw_id,
              game.draw_date
            )
            continue
          }

          for (const bet of game.bets) {
            const match = matchWagerBetToMarket(bet, marketGroups)
            const matchedMarket = match?.matchedMarket ?? null
            const matchedOutcome = match?.matchedOutcome ?? null
            const matchedMarketGroup = match?.matchedMarketGroup ?? null

            if (!matchedMarket || !matchedOutcome || !matchedMarketGroup) {
              console.warn('Could not find matching market/outcome for bet:', bet)
              continue
            }

            const betslipItem: BetslipItem = {
              market_id: matchedMarket.id,
              market_group_id: matchedMarketGroup.id,
              ...(bet.game_market_id != null
                ? { game_market_id: Number(bet.game_market_id) }
                : {}),
              game_name: gameStore.getGameDetail?.name,
              game_id: gameStore.getGameDetail?.id || 0,
              market_name: bet.market_name || '',
              market_description: bet.market_group_description || '',
              numbers: bet.bet_numbers || [],
              stake: Number(bet.bet_cost),
              betType: bet.betType,
              maxStake: Number(gameStore.getBetSettings?.bet_max) ?? 10000,
              odds: Number(bet.odds),
              draws: toBetslipDraws(selectedDraws),
              allAvailableDraws: toBetslipDraws(upcomingDraws),
              drawCount: selectedDraws.length,
              picks: (bet as any).picks || 0,
              numBets: 1,
              outcome_id: matchedOutcome.id.toString(),
              label: bet.market_name || '',
              specifier: matchedOutcome.specifier || null,
              select_numbers: matchedOutcome.select_numbers || false,
              currency_symbol:
                gameStore.getBetSettings?.currency_symbol ||
                (transaction as any).currency_symbol ||
                '€',
            }

            const key = rebetLineKey(betslipItem)
            const existing = preparedLines.get(key)
            if (existing) {
              existing.draws = mergeRebetDraws(existing.draws, betslipItem.draws)
              existing.drawCount = existing.draws.length
            } else {
              preparedLines.set(key, betslipItem)
            }
          }
        }
      }

      if (preparedLines.size === 0) {
        showToast({
          title: t('betslip.rebetUnavailableTitle'),
          message:
            skippedUnavailableDraws > 0
              ? t('betslip.rebetDrawUnavailable')
              : t('betslip.rebetNothingAdded'),
          type: 'error',
          duration: 6000,
        })
        return
      }

      for (const line of preparedLines.values()) {
        const { changed, item } = betslipStore.addRebet(line)
        if (changed) {
          iframeCommunication.sendMessage('bets-added', { bets: [item] })
        }
      }

      onComplete?.()
      if (openBetslip) {
        modalStore.openBetslip()
      }
    } catch (error) {
      console.error('Rebet error:', error)
      showToast({
        title: t('betslip.rebetUnavailableTitle'),
        message: t('betslip.failedToAddBets'),
        type: 'error',
        duration: 6000,
      })
    } finally {
      rebetting.value = false
    }
  }

  return { applyRebetFromCurrentTransaction, isRebetting: rebetting }
}
